import { IPlayerState } from '../IPlayerState';
import { getCurrentLevelNumber } from '../PlayerLevelHelper';
import { LevelType } from '../Progress/PlayerLevels';
import { PlayerStateHelper } from '../PlayerStateHelper';
import { PlayerTask } from '../PlayerTasks';
import { ProgressStateHelper } from '../Progress/ProgressStateHelper';
import { STAR_CHEST_SPAWN_INCREMENT, STAR_CHEST_TIER_DATA } from 'Constants/Chests';
import { IGameState } from 'State/Game/IGameState';
import { Immutable } from 'Utility/Types';
import { MathHelper } from 'Utility/MathHelper';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import { StarChestHelper } from 'Utility/StarChestHelper';

export namespace StarChestStateHelper {
    export const getStarChestStarCount = (playerState: IPlayerState): number => {
        if (playerState.starChestLevel === null) return 0;
        return (
            ProgressStateHelper.getStarsCount(playerState, LevelType.Core) - playerState.progress.starChestInitialStars
        );
    };

    export const getStarChestUnlockTier = (playerState: IPlayerState, ignoreNStars: number = 0): number => {
        const starCount = StarChestStateHelper.getStarChestStarCount(playerState) - ignoreNStars;

        const maxStarCount = Math.max(0, starCount);
        const sortedRequirements = [...STAR_CHEST_TIER_DATA].sort((a, b) => b.requiredStars - a.requiredStars);
        const lastUnlockedTier = sortedRequirements.find(tierData => {
            return tierData.requiredStars <= maxStarCount;
        });

        return STAR_CHEST_TIER_DATA.indexOf(lastUnlockedTier);
    };

    export const getLevelToSpawnStarChestOn = (state: Immutable<IGameState>): number | null => {
        // If player already has a star chest, don't spawn new one
        if (state.playerState.starChestLevel !== null && state.playerState.starChestLevel > 0) return null;

        const currentLevel = getCurrentLevelNumber(state.playerState);

        const levelToSpawnStarChestOn = MathHelper.floor(
            currentLevel + STAR_CHEST_SPAWN_INCREMENT,
            // floor to increments of starChestSpawnIncrement
            STAR_CHEST_SPAWN_INCREMENT
        );

        // FTUE edge case
        if (!FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.STAR_CHEST)) {
            const ftueCanBeShown =
                WorldDataHelper.getWorldNumber(levelToSpawnStarChestOn) ===
                WorldDataHelper.getWorldNumber(currentLevel) &&
                levelToSpawnStarChestOn <= WorldDataHelper.maxLevel();
            // Don't spawn new chest if FTUE wasn't show and we can't show it yet
            if (!ftueCanBeShown) {
                return null;
            } else {
                // If we weren't able to previously show FTUE, spawn a star chest once we can
                // Also covers brand new users

                // Assuming that user can still get all stars from `currentLevel`
                const levelsUntilStarChest = levelToSpawnStarChestOn - currentLevel;
                const maxStarsPerLevel = 3;
                // If levels that are left between current and star chest are not enough
                // to get the highest tier reward, don't spawn chest this time
                if (levelsUntilStarChest * maxStarsPerLevel < StarChestHelper.getStarCountCap() && currentLevel < STAR_CHEST_SPAWN_INCREMENT) {
                    return null;
                }
                return levelToSpawnStarChestOn;
            }
        }

        return levelToSpawnStarChestOn;
    };

    export const setStarChestInitialStars = (
        playerState: IPlayerState,
        starChestInitialStars: number
    ): IPlayerState => {
        return {
            ...playerState,
            progress: {
                ...playerState.progress,
                starChestInitialStars,
            },
        };
    };

    export const setStarChestLevel = (oldPlayerState: IPlayerState, starChestLevel: number | null): IPlayerState => {
        let playerState = oldPlayerState;
        playerState = queueStarChestFTUXIfNeeded(playerState);
        if (starChestLevel !== null) {
            playerState = setStarChestInitialStars(
                playerState,
                ProgressStateHelper.getStarsCount(playerState, LevelType.Core)
            );
        }
        return {
            ...playerState,
            starChestLevel,
        };
    };

    export const queueStarChestFTUXIfNeeded = (playerState: IPlayerState): IPlayerState => {
        const worldLimits = WorldDataHelper.getWorldLevelLimits(playerState.maxWorld);
        if (
            playerState.starChestLevel !== null &&
            !FTUXStateHelper.isFtuxCompleted(playerState, FTUXType.STAR_CHEST) &&
            worldLimits &&
            worldLimits.max >= playerState.starChestLevel &&
            playerState.tasks.find(task => task.task === PlayerTask.STAR_CHEST_FTUX) === undefined
        ) {
            return PlayerStateHelper.addStateTaskDatas(playerState, [
                {
                    task: PlayerTask.STAR_CHEST_FTUX,
                    starChestLevel: playerState.starChestLevel,
                },
            ]);
        }
        return playerState;
    };
}
